import { html, css } from 'lit';
import { customElement, state, query, queryAll } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { consume } from '@lit/context';
import { publicLedgerServiceContext, nsLoaderContext } from '../../../../../ns-services/contexts.js';

import { PublicLedgerService } from '../../../../../ns-services/ns-public-ledger-service.js';
import { NSLoader } from '../../../../../ns-services/ns-loader-service.js';

import JobSchema from '../../../../../models/job-schema.js';
import type Workflow from '../../../../../models/workflow.js';
import { savesPhraseLetters } from '../../../../../helpers/phrase-check-api.js';

import NSJobComponent from '../../../job.js';
import { LiveAnnouncer } from '../../../../../helpers/live-announcer.js';
import '../../../../../ns-elements/ns-job-layout.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/public-ledger/account/recover-keys.json';
import { jobSchema } from '../../../job-schema.js';

@customElement(NSRecoverPublicLedgerAccountKeys.is)
export default class NSRecoverPublicLedgerAccountKeys extends NSJobComponent {
  static get is() {
    return 'ns-recover-public-ledger-account-keys';
  }

  @consume({ context: publicLedgerServiceContext })
  private __pLedgerService?: PublicLedgerService;

  @consume({ context: nsLoaderContext })
  private __loader?: NSLoader;

  @queryAll('.word-input')
  private __wordInputs?: NodeListOf<HTMLInputElement>;

  @query('.stage-heading')
  private __stageHeading?: HTMLElement;

  @state()
  private __mnemonic: string[] = Array.from({ length: 24 });

  private __announcer = new LiveAnnouncer(this);

  static styles = [
    ...super.styles,
    css`
      .stage-heading {
        margin: 0;
        font-size: var(--wa-font-size-l);
        font-weight: 500;
      }

      /* Grey-dark and purple borders keep the 3:1 non-text contrast on the white field and grey panel */
      .word-input {
        background-color: var(--wa-color-surface-default);
        color: var(--wa-color-text-normal);
        width: 100px;
        min-width: 0;
        height: 45px;
        font-size: var(--wa-font-size-m);
        text-align: center;
        border: 2px solid var(--wa-color-neutral-border-loud);
        border-radius: var(--wa-border-radius-m);
        outline: none;
        transition: border-color 0.3s ease-in-out;
      }
      .word-input.filled {
        border-color: var(--wa-color-brand-border-loud);
      }
      .word-input:focus-visible {
        outline: 2px solid var(--wa-color-focus);
        outline-offset: 2px;
      }

      .words-grid {
        display: grid;
        /* Fits as many number + 100px box columns as the column has room for. */
        grid-template-columns: repeat(auto-fill, minmax(8rem, 1fr));
        gap: var(--wa-space-m);
        align-items: start;

        margin: 0;
        padding: 0;
        list-style: none;
      }

      .words-grid > .layout {
        display: flex;
        align-items: center;
        gap: var(--wa-space-2xs);
      }

      #mnemonic-label {
        margin: 0;
      }

      /* The heading and clear button row, spaced from the words as the grid spaces its own rows */
      .words-header {
        margin-block-end: var(--wa-space-m);
      }

      .words-grid .word-idx {
        flex: 0 0 2.5ch;
        text-align: end;
      }

      .stage-heading:focus,
      .stage-heading:focus-visible {
        outline: none;
      }
    `,
  ];

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { complete: { type: 'hidden' } } });
  }

  async connectedCallback() {
    await super.connectedCallback();
    await this.updateComplete;

    if (!this.__pLedgerService) throw new Error('Unable to find public ledger service');

    if (!this.__loader) throw new Error('Loader service not found');
  }

  private async __recoverAccount() {
    if (!this._job || !this._job.id) throw new Error('Missing required job');
    this.clearError();

    try {
      if (!this.__mnemonic.every((w) => w)) throw new Error('Please enter your 24 words recovery phrase.');
      if (!this.__pLedgerService) throw new Error('Unable to find public ledger service');

      this.loading('Recovering a public ledger account keys');
      const mnemonic = this.__mnemonic.join(' ');
      await this.__pLedgerService.recoverAccount(mnemonic);
      // The phrase has just been typed and checked, so the server records it: on account creation's card the letters later
      // checks ask for, on any other only a check's 4 letters; and verifiedAt
      await this.__pLedgerService.recordPhraseChecked(this._job.id, mnemonic, await this.__savesPhraseLetters());

      this.loaded();
      this.outcome('complete');
    } catch (err) {
      this.loaded();
      this.showError(err);
    }
  }

  // Only account creation's card saves the phrase's letters. A card with no workflow found sends a check, never letters
  private async __savesPhraseLetters(): Promise<boolean> {
    if (!this._db || !this._job?.workflow?.id) return false;
    const workflow = (await this._db.getById('workflow', this._job.workflow.id)) as Workflow | undefined;
    return savesPhraseLetters(workflow?.workflowName);
  }

  private __updateWord(idx: number, value: string) {
    this.__mnemonic[idx] = value.trim();
    this.requestUpdate();

    if (!this._job || !this._job.id) return;
    this.clearError();
  }

  private __handlePhrasePaste(event: ClipboardEvent) {
    const pasted = event.clipboardData?.getData('text') ?? '';
    const words = pasted.trim().split(/\s+/).filter(Boolean);
    if (words.length <= 1) return;

    event.preventDefault();

    const filled = words.slice(0, 24);
    this.__mnemonic = [...filled, ...Array.from({ length: 24 - filled.length })] as string[];
    this.__announcer.announce(`Recovery phrase pasted — ${filled.length} of 24 words filled in.`);

    this.updateComplete.then(() => {
      this.__wordInputs?.[Math.min(filled.length, 24) - 1]?.focus();
    });
  }

  private __clearMnemonic() {
    this.__mnemonic = Array.from({ length: 24 });
    this.__announcer.announce('All 24 words cleared.');

    if (this._job && this._job.id) {
      this.clearError();
    }

    this.updateComplete.then(() => {
      this.__wordInputs?.[0]?.focus();
    });
  }

  private __renderInfo() {
    return html`
      <div slot="aside" class="primary-info">
        <span class="info-icon" aria-hidden="true">i</span>
        <h3>Your recovery phrase</h3>
        <p>
          Enter the 24-word recovery phrase you saved in a secure place when you set up your public ledger account. It's
          the only way to recover the account.
        </p>
        <h3>Paste it in one go</h3>
        <p>Paste your full 24-word phrase into the first word to fill in the rest automatically.</p>
        <h3>Keep it private</h3>
        <p>
          Your recovery phrase isn't stored on our servers, and even our team can't retrieve it. Never share it with
          anyone.
        </p>
      </div>
    `;
  }

  private __renderWords() {
    return html`
      <div class="job-panel">
        <div class="words-header layout horizontal center">
          <h3 class="flex" id="mnemonic-label">Your 24 words</h3>
          <wa-button
            type="button"
            variant="brand"
            appearance="outlined"
            class="icon-button circle clear-mnemonic"
            @click=${() => this.__clearMnemonic()}
            ?disabled=${!this.__mnemonic.some((word) => word && word.trim())}
          >
            <svg viewBox="0 0 4.5 4.5" width="1em" height="1em" aria-hidden="true">
              <path
                d="M0.18 0.18L4.18 4.18M4.18 0.18L2.18 2.18L0.18 4.18"
                stroke="currentColor"
                stroke-width="0.5"
                fill="none"
              />
            </svg>
            <span class="visually-hidden">Clear all</span>
          </wa-button>
        </div>

        <div class="words-grid" role="group" aria-labelledby="mnemonic-label">
          ${repeat(
            this.__mnemonic,
            (_word, idx) => idx,
            (word, idx) => html`
              <div class="layout">
                <label class="word-idx" for="mnemonic-word-${idx}">${idx + 1}.</label>
                <input
                  id="mnemonic-word-${idx}"
                  class="word-input ${word ? 'filled' : ''}"
                  type="text"
                  autocomplete="off"
                  autocapitalize="off"
                  autocorrect="off"
                  spellcheck="false"
                  .value=${word ?? ''}
                  @input=${(e: Event) => this.__updateWord(idx, (e.target as HTMLInputElement).value)}
                  @paste=${idx === 0 ? (e: ClipboardEvent) => this.__handlePhrasePaste(e) : undefined}
                />
              </div>
            `,
          )}
        </div>
        ${this.__announcer.render()}
      </div>
    `;
  }

  render() {
    return html`
      <ns-job-layout>
        <h2 slot="heading" class="stage-heading" tabindex="-1">Enter your recovery phrase</h2>
        ${this.__renderWords()} ${this.__renderInfo()}
      </ns-job-layout>
    `;
  }

  renderActions() {
    return html`
      <wa-button variant="brand" type="button" @click=${() => this.__recoverAccount()}>Confirm</wa-button>
    `;
  }
}
